import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import {
  CmpMemberLifecycleStatusEnum,
  GetCmpManageMembersQuery,
  useArchiveCmpMemberMutation,
  useGetCmpManageMembersQuery,
  useGetCmpMemberDirectoryExportLazyQuery,
} from "../../../../graphql/types";
import { RoutePaths } from "../../../../enumStrings";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { downloadTextFile } from "../../Invoicing/invoiceFileDownloads";
import {
  cpaGold,
  cpaGoldDark,
  getUpdateMemberNavigationState,
} from "../memberWorkflowShared";
import { getMembershipTypeDisplayLabel } from "../memberProfileMembership";
import { commonStyles } from "../../../common/commonStyles";
import {
  cmpLongDateFormatter,
  formatUtcCalendarDate,
  getUtcCalendarDateEndUtcMilli,
  getUtcCalendarDateStartUtcMilli,
} from "../../../common/dashboard/formatters";
import {
  DataTable,
  DataTableColumn,
} from "../../../common/dashboard/DataTable";
import {
  ClearFiltersButton,
  FilterBar,
  FilterMenuButton,
  FilterSearchField,
} from "../../../common/dashboard/FilterBar";
import {
  ManagePageSection,
  ManagePageShell,
} from "../../../common/dashboard/ManagePageShell";
import { StatusBadge } from "../../../common/dashboard/StatusBadge";
import CalendarDateInput from "../../MyChamber/ChamberSettings/common/CalendarDateInput";

type SortDirection = "asc" | "desc";

type SortableColumnKey =
  | "companyName"
  | "memberStatus"
  | "dateJoined"
  | "membershipType"
  | "memberSize"
  | "renewalDate";

type ManageMembersColumnKey = SortableColumnKey | "actions";

type BackendMemberRow =
  GetCmpManageMembersQuery["getCmpManageMembers"]["rows"][number];

type ManageMemberRow = {
  dateJoined: string;
  dateJoinedUtcMilli?: number | null;
  fullTimeEmployees?: number | null;
  id: string;
  lifecycleStatus: CmpMemberLifecycleStatusEnum;
  memberSize: number;
  memberStatus: string;
  membershipType: string;
  partTimeEmployees?: number | null;
  renewalDate: string;
  renewalDateUtcMilli?: number | null;
  searchText: string;
  companyName: string;
};

type AnalyticsMetricCard = {
  currentValue: number;
  formatter?: (value: number) => string;
  label: string;
  positiveChangeIsGood: boolean;
  previousValue: number;
};

type StatusMessage = {
  severity: "success" | "info" | "error";
  message: string;
};

type ManageMembersLocationState = {
  statusMessage?: StatusMessage;
};

const todayInputValue = () => new Date().toISOString().slice(0, 10);
const defaultArchiveReason = "Deleted from Manage Members";

// The filter-control styling (search field / filter buttons / clear button)
// was promoted verbatim to common/dashboard/FilterBar.tsx — its defaults ARE
// this page's values.

const tableActionButtonSx = {
  borderRadius: "4px",
  boxShadow: "none",
  fontSize: 11,
  fontWeight: 800,
  justifyContent: "center",
  lineHeight: 1,
  minHeight: 28,
  minWidth: 84,
  overflowWrap: "normal",
  px: 1.25,
  py: 0.75,
  textTransform: "none",
  whiteSpace: "nowrap",
  width: 84,
  "& .MuiButton-startIcon": {
    marginLeft: 0,
    marginRight: 0.75,
    "& svg": { fontSize: 14 },
  },
  "&:hover": { boxShadow: "none" },
};

// Mirrors the gold page-header action styling PageContainer uses for its
// default header actions (e.g. Manage Invoices "Create New Invoice").
const pageHeaderActionButtonSx = {
  alignSelf: { xs: "flex-start", sm: "center" },
  backgroundColor: cpaGold,
  borderRadius: "6px",
  color: "white",
  fontSize: 16,
  fontWeight: 800,
  minHeight: 52,
  px: 3,
  textTransform: "none",
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": {
    mr: 1,
    "& svg": { fontSize: 22 },
  },
  "&:hover": { backgroundColor: cpaGoldDark },
};
const defaultStatuses: CmpMemberLifecycleStatusEnum[] = [
  CmpMemberLifecycleStatusEnum.Pending,
  CmpMemberLifecycleStatusEnum.Active,
];
// Order matches the design's status filter popup: Prospect, Pending,
// Active, Suspended, Past.
const allStatuses: CmpMemberLifecycleStatusEnum[] = [
  CmpMemberLifecycleStatusEnum.Prospect,
  CmpMemberLifecycleStatusEnum.Pending,
  CmpMemberLifecycleStatusEnum.Active,
  CmpMemberLifecycleStatusEnum.Suspended,
  CmpMemberLifecycleStatusEnum.Past,
];
const filterableStatusSet = new Set(allStatuses);

const statusLabels: Record<CmpMemberLifecycleStatusEnum, string> = {
  [CmpMemberLifecycleStatusEnum.Active]: "Active",
  [CmpMemberLifecycleStatusEnum.Deleted]: "Past",
  [CmpMemberLifecycleStatusEnum.Past]: "Past",
  [CmpMemberLifecycleStatusEnum.Pending]: "Pending",
  [CmpMemberLifecycleStatusEnum.Prospect]: "Prospect",
  [CmpMemberLifecycleStatusEnum.Suspended]: "Suspended",
};

const statusTextClassNames: Record<string, string> = {
  Active: "text-green-700",
  Past: "text-red-600",
  Pending: "text-cpaGold",
  Prospect: "text-gray-950",
  // Suspended must be visually distinct from Active's green per the design.
  Suspended: "text-orange-600",
};

const currencyFormatter = new Intl.NumberFormat("en-CA", {
  currency: "CAD",
  style: "currency",
});

const integerFormatter = new Intl.NumberFormat("en-CA", {
  maximumFractionDigits: 0,
});

const dateFormatter = cmpLongDateFormatter;

const statusSetsMatch = (
  firstStatuses: CmpMemberLifecycleStatusEnum[],
  secondStatuses: CmpMemberLifecycleStatusEnum[]
) =>
  firstStatuses.length === secondStatuses.length &&
  firstStatuses.every((status) => secondStatuses.includes(status));

const centsToDollars = (amountInCents?: number | null) =>
  amountInCents === undefined || amountInCents === null
    ? 0
    : amountInCents / 100;

const formatCurrency = (amount: number) => currencyFormatter.format(amount);

const formatInteger = (amount: number) =>
  integerFormatter.format(Math.round(amount));

const normalizeSearchText = (value: string | null | undefined) =>
  (value || "").trim().toLowerCase();

const formatDate = (date?: number | string | null) => {
  if (!date) {
    return "-";
  }

  return dateFormatter.format(
    typeof date === "number" ? new Date(date) : new Date(`${date}T00:00:00`)
  );
};

const toDisplayMember = (row: BackendMemberRow): ManageMemberRow => ({
  companyName: row.companyName,
  dateJoined: formatDate(row.dateJoinedUtcMilli),
  dateJoinedUtcMilli: row.dateJoinedUtcMilli,
  fullTimeEmployees: row.fullTimeEmployees,
  id: row.id,
  lifecycleStatus: row.lifecycleStatus,
  memberSize: (row.fullTimeEmployees || 0) + (row.partTimeEmployees || 0),
  memberStatus: statusLabels[row.lifecycleStatus],
  membershipType: getMembershipTypeDisplayLabel(row.membershipTypeName),
  partTimeEmployees: row.partTimeEmployees,
  // The mock shows renewal dates for Active AND Pending rows; Past, Suspended
  // and Prospect rows render "-".
  renewalDate:
    row.lifecycleStatus === CmpMemberLifecycleStatusEnum.Active ||
    row.lifecycleStatus === CmpMemberLifecycleStatusEnum.Pending
      ? row.renewalDateUtcMilli
        ? formatUtcCalendarDate(row.renewalDateUtcMilli)
        : "-"
      : "-",
  renewalDateUtcMilli: row.renewalDateUtcMilli,
  searchText: row.searchText,
});

const getMemberSortValue = (
  member: ManageMemberRow,
  column: SortableColumnKey
): string | number => {
  switch (column) {
    case "memberStatus":
      return member.memberStatus;
    case "dateJoined":
      return member.dateJoinedUtcMilli || 0;
    case "membershipType":
      return member.membershipType;
    case "memberSize":
      return member.memberSize;
    case "renewalDate":
      return member.renewalDateUtcMilli || 0;
    case "companyName":
    default:
      return member.companyName;
  }
};

const sortMembers = ({
  column,
  direction,
  members,
}: {
  column: SortableColumnKey;
  direction: SortDirection;
  members: ManageMemberRow[];
}) =>
  [...members].sort((firstMember, secondMember) => {
    const firstValue = getMemberSortValue(firstMember, column);
    const secondValue = getMemberSortValue(secondMember, column);
    let result = 0;

    if (typeof firstValue === "number" && typeof secondValue === "number") {
      result = firstValue - secondValue;
    } else {
      result = String(firstValue).localeCompare(String(secondValue));
    }

    if (result === 0) {
      result = firstMember.companyName.localeCompare(secondMember.companyName);
    }

    return direction === "desc" ? -result : result;
  });

const getMemberSizeLines = (member: ManageMemberRow) => {
  const fullTimeEmployees = member.fullTimeEmployees || 0;
  const partTimeEmployees = member.partTimeEmployees || 0;

  if (!fullTimeEmployees && !partTimeEmployees) {
    return ["-"];
  }

  return [
    `${formatInteger(fullTimeEmployees)} Full Time`,
    `${formatInteger(partTimeEmployees)} Part Time`,
  ];
};

const getTrend = ({
  currentValue,
  positiveChangeIsGood,
  previousValue,
}: Pick<
  AnalyticsMetricCard,
  "currentValue" | "positiveChangeIsGood" | "previousValue"
>) => {
  if (previousValue === 0 && currentValue === 0) {
    return {
      className: "text-gray-600",
      text: "No change from last year",
    };
  }

  if (previousValue === 0) {
    return {
      className: positiveChangeIsGood ? "text-green-500" : "text-red-500",
      text: "New this year",
    };
  }

  const percentChange = Math.round(
    (Math.abs(currentValue - previousValue) / Math.abs(previousValue)) * 100
  );
  const increased = currentValue >= previousValue;
  const isGoodChange = increased ? positiveChangeIsGood : !positiveChangeIsGood;

  return {
    className: isGoodChange ? "text-green-500" : "text-red-500",
    text: `${percentChange}% ${increased ? "Increase" : "decrease"} from last year`,
  };
};

const ManageMembers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [archiveSuccessOpen, setArchiveSuccessOpen] = useState(false);
  const [memberToArchive, setMemberToArchive] =
    useState<ManageMemberRow | null>(null);
  const [renewalDateFrom, setRenewalDateFrom] = useState("");
  const [renewalDateTo, setRenewalDateTo] = useState("");
  const [renewalMenuAnchor, setRenewalMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] =
    useState<CmpMemberLifecycleStatusEnum[]>(defaultStatuses);
  const [sortColumn, setSortColumn] =
    useState<SortableColumnKey>("companyName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(
    null
  );
  const manageMembersQueryVariables = useMemo(
    () => ({
      input: {
        chamberId,
        lifecycleStatuses: allStatuses,
      },
    }),
    [chamberId]
  );

  const {
    data: manageMembersData,
    loading: manageMembersLoading,
    error: manageMembersError,
    refetch: refetchManageMembers,
  } = useGetCmpManageMembersQuery({
    variables: manageMembersQueryVariables,
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const [archiveCmpMember, { loading: archivingMember }] =
    useArchiveCmpMemberMutation();
  const [getDirectoryExport, { loading: directoryExportLoading }] =
    useGetCmpMemberDirectoryExportLazyQuery({
      fetchPolicy: "network-only",
    });

  const allMembers = useMemo(
    () =>
      (manageMembersData?.getCmpManageMembers.rows || []).map(toDisplayMember),
    [manageMembersData]
  );
  const members = useMemo(() => {
    const normalizedSearchQuery = normalizeSearchText(searchQuery);
    const renewalDateFromUtcMilli = renewalDateFrom
      ? getUtcCalendarDateStartUtcMilli(renewalDateFrom)
      : null;
    const renewalDateToUtcMilli = renewalDateTo
      ? getUtcCalendarDateEndUtcMilli(renewalDateTo)
      : null;
    const filteredMembers = allMembers
      .filter((member) => selectedStatuses.includes(member.lifecycleStatus))
      .filter((member) =>
        normalizedSearchQuery
          ? normalizeSearchText(member.searchText).includes(
              normalizedSearchQuery
            )
          : true
      )
      .filter((member) =>
        renewalDateFromUtcMilli
          ? !!member.renewalDateUtcMilli &&
            member.renewalDateUtcMilli >= renewalDateFromUtcMilli
          : true
      )
      .filter((member) =>
        renewalDateToUtcMilli
          ? !!member.renewalDateUtcMilli &&
            member.renewalDateUtcMilli <= renewalDateToUtcMilli
          : true
      );

    return sortMembers({
      column: sortColumn,
      direction: sortDirection,
      members: filteredMembers,
    });
  }, [
    allMembers,
    renewalDateFrom,
    renewalDateTo,
    searchQuery,
    selectedStatuses,
    sortColumn,
    sortDirection,
  ]);
  const analytics = manageMembersData?.getCmpManageMembers.analytics;
  const analyticsCards: AnalyticsMetricCard[] = analytics
    ? [
        {
          currentValue: analytics.totalMembersYtd,
          label: "Total Number of Members YTD",
          positiveChangeIsGood: true,
          previousValue: analytics.previousFiscalYearTotalMembersYtd,
        },
        {
          currentValue: analytics.totalNewMembersYtd,
          label: "Total New Members YTD",
          positiveChangeIsGood: true,
          previousValue: analytics.previousFiscalYearNewMembersYtd,
        },
        {
          currentValue: analytics.totalMemberCancellationsYtd,
          label: "Total Member Cancellations YTD",
          positiveChangeIsGood: false,
          previousValue:
            analytics.previousFiscalYearMemberCancellationsYtd,
        },
        {
          currentValue: analytics.totalSuspendedMembersYtd,
          label: "Total Suspended Members YTD",
          positiveChangeIsGood: false,
          previousValue: analytics.previousFiscalYearSuspendedMembersYtd,
        },
        {
          currentValue: analytics.totalNewProspectMembersYtd,
          label: "Total New Prospect Member YTD",
          positiveChangeIsGood: true,
          previousValue:
            analytics.previousFiscalYearNewProspectMembersYtd,
        },
        {
          currentValue: centsToDollars(
            analytics.totalMembershipRevenueYtdInCents
          ),
          formatter: formatCurrency,
          label: "Total Membership Revenue YTD",
          positiveChangeIsGood: true,
          previousValue: centsToDollars(
            analytics.previousFiscalYearMembershipRevenueYtdInCents
          ),
        },
      ]
    : [];
  const defaultVisibleStatuses =
    manageMembersData?.getCmpManageMembers.defaultLifecycleStatuses.filter(
      (status) => filterableStatusSet.has(status)
    ) || defaultStatuses;
  const statusFilterIsActive = !statusSetsMatch(
    selectedStatuses,
    defaultVisibleStatuses
  );
  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    renewalDateFrom.length > 0 ||
    renewalDateTo.length > 0 ||
    statusFilterIsActive;

  useEffect(() => {
    const incomingStatusMessage = (
      location.state as ManageMembersLocationState | null
    )?.statusMessage;

    if (!incomingStatusMessage) {
      return;
    }

    setStatusMessage(incomingStatusMessage);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const requestSort = (column: SortableColumnKey) => {
    const isSameColumn = sortColumn === column;
    setSortColumn(column);
    setSortDirection(isSameColumn && sortDirection === "asc" ? "desc" : "asc");
  };

  const toggleStatus = (status: CmpMemberLifecycleStatusEnum) => {
    setSelectedStatuses((currentStatuses) =>
      currentStatuses.includes(status)
        ? currentStatuses.filter((currentStatus) => currentStatus !== status)
        : [...currentStatuses, status]
    );
  };

  const clearFilters = () => {
    setRenewalDateFrom("");
    setRenewalDateTo("");
    setSearchQuery("");
    setSelectedStatuses(defaultVisibleStatuses);
    setSortColumn("companyName");
    setSortDirection("asc");
    setStatusMessage(null);
  };

  const handleArchiveClick = (member: ManageMemberRow) => {
    setStatusMessage(null);
    setMemberToArchive(member);
  };

  const handleArchiveConfirm = async () => {
    if (!chamberId || !memberToArchive) {
      return;
    }

    try {
      await archiveCmpMember({
        variables: {
          input: {
            archivedAtUtcMilli: getUtcCalendarDateStartUtcMilli(
              todayInputValue()
            ),
            archiveReason: defaultArchiveReason,
            chamberId,
            memberId: memberToArchive.id,
          },
        },
      });
      setMemberToArchive(null);
      setArchiveSuccessOpen(true);
      await refetchManageMembers();
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Could not move this member to Past.",
      });
    }
  };

  const exportMemberDirectory = useCallback(async () => {
    if (!chamberId) {
      setStatusMessage({
        severity: "error",
        message: "Chamber context is still loading. Try again in a moment.",
      });
      return;
    }

    try {
      const result = await getDirectoryExport({
        variables: {
          input: { chamberId },
        },
      });
      const exportResult = result.data?.getCmpMemberDirectoryExport;

      if (!exportResult) {
        throw new Error("No directory export response was returned.");
      }

      downloadTextFile({
        filename: exportResult.filename,
        contentType: exportResult.contentType,
        content: exportResult.csvContent,
      });

      setStatusMessage({
        severity: "success",
        message: `${exportResult.filename} prepared and downloaded at ${formatDate(
          exportResult.generatedAtUtcMilli
        )}.`,
      });
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "Could not prepare the member directory export.",
      });
    }
  }, [chamberId, getDirectoryExport]);

  // Page-header action buttons live in the PageContainer header zone (same
  // pattern as Manage Invoices) instead of the Member Lists section header.
  const pageHeaderActions = useMemo(
    () => (
      <div className="flex flex-wrap justify-end gap-4">
        <Button
          type="button"
          variant="contained"
          startIcon={<FileUploadOutlinedIcon />}
          onClick={() => void exportMemberDirectory()}
          disabled={directoryExportLoading || !chamberId}
          sx={pageHeaderActionButtonSx}
        >
          {directoryExportLoading
            ? "Preparing Export..."
            : "Export Member Directory"}
        </Button>
        <Button
          type="button"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(RoutePaths.chamberAdmin_members_create)}
          sx={pageHeaderActionButtonSx}
        >
          Add New Member
        </Button>
      </div>
    ),
    [chamberId, directoryExportLoading, exportMemberDirectory, navigate]
  );

  // Keep the route-configured title/description; only inject the actions
  // and the divider under the page header block per the design.
  const headerOverride = useMemo(
    () => ({
      actions: pageHeaderActions,
      withHeaderDivider: true,
    }),
    [pageHeaderActions]
  );

  // Column defaults (widths, sort labels, cell markup) are the values this
  // page rendered before the DataTable promotion — the shared component's
  // defaults are byte-identical copies.
  const columns: DataTableColumn<ManageMemberRow, ManageMembersColumnKey>[] = [
    {
      key: "companyName",
      header: "Member Company",
      sortable: true,
      width: "20%",
      render: (member) => (
        <p className="text-sm font-bold text-gray-950">{member.companyName}</p>
      ),
    },
    {
      key: "memberStatus",
      header: "Member Status",
      sortable: true,
      width: "13%",
      render: (member) => (
        <StatusBadge
          variant="text"
          label={member.memberStatus}
          textClassName={`text-sm font-bold ${
            statusTextClassNames[member.memberStatus] || "text-gray-950"
          }`}
        />
      ),
    },
    {
      key: "dateJoined",
      header: "Date Joined",
      sortable: true,
      width: "12%",
      render: (member) => member.dateJoined,
    },
    {
      key: "membershipType",
      header: "Membership Type",
      sortable: true,
      width: "17%",
      render: (member) => member.membershipType,
    },
    {
      key: "memberSize",
      header: "Member Size",
      sortable: true,
      width: "14%",
      render: (member) =>
        getMemberSizeLines(member).map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        )),
    },
    {
      key: "renewalDate",
      header: "Renewal Date",
      sortable: true,
      width: "12%",
      render: (member) => member.renewalDate,
    },
    {
      key: "actions",
      header: "Actions",
      headerCellSx: { fontWeight: 800, width: "12%" },
      stickyRight: true,
      render: (member) => (
        <div className="flex flex-col items-start gap-2">
          <Button
            type="button"
            size="small"
            variant="contained"
            startIcon={<EditOutlinedIcon />}
            onClick={() =>
              navigate(RoutePaths.chamberAdmin_members_update, {
                state: getUpdateMemberNavigationState(member.id),
              })
            }
            sx={{
              ...tableActionButtonSx,
              backgroundColor: cpaGold,
              color: "white",
              "&:hover": {
                backgroundColor: cpaGoldDark,
              },
            }}
          >
            Update
          </Button>
          <Button
            type="button"
            size="small"
            variant="contained"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => handleArchiveClick(member)}
            sx={{
              ...tableActionButtonSx,
              backgroundColor: "#F31723",
              "&:hover": { backgroundColor: "#D90F1A" },
            }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <ManagePageShell
      headerOverride={headerOverride}
      statusMessage={statusMessage}
      onStatusMessageClose={() => setStatusMessage(null)}
    >
      <ManagePageSection
        className="-mx-8 px-8 pt-6"
        title="Total Member Analytics (Year to Date)"
        description="See a quick overview of your chamber total year to date membership analytics at a glance."
        descriptionClassName="mt-1 text-sm text-black"
      >
        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-6">
          {analyticsCards.length > 0 ? (
            analyticsCards.map((card) => {
              const trend = getTrend(card);

              return (
                <article
                  key={card.label}
                  className="min-h-[132px] rounded-lg border border-gray-300 p-5"
                >
                  <h3 className="text-[14px] font-bold leading-5 text-black">
                    {card.label}
                  </h3>
                  <p className="mt-3 text-2xl font-bold text-gray-950">
                    {card.formatter
                      ? card.formatter(card.currentValue)
                      : formatInteger(card.currentValue)}
                  </p>
                  <p className={`mt-3 text-sm font-bold ${trend.className}`}>
                    {trend.text}
                  </p>
                </article>
              );
            })
          ) : (
            <div className="col-span-full flex items-center gap-3 rounded-lg border border-gray-200 p-5 text-sm font-semibold text-gray-500">
              <CircularProgress size={20} />
              Loading fiscal year analytics...
            </div>
          )}
        </div>
      </ManagePageSection>

      <ManagePageSection
        className="mt-10 -mx-8 px-8"
        title="Member Filters"
        description="Search members name or contact details (e.g.: Member Name, Email, Phone Number)"
      >
        <FilterBar>
          <FilterSearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Enter member name or member details"
          />
          <FilterMenuButton
            label="Filter by Member Status"
            onOpen={(anchorEl) => setStatusMenuAnchor(anchorEl)}
            active={statusFilterIsActive}
            badgeCount={statusFilterIsActive ? selectedStatuses.length : null}
            badgeAriaLabel={`${selectedStatuses.length} statuses selected`}
            wrapperClassName="xl:w-[260px]"
          />
          <FilterMenuButton
            label="Filter by Renewal Date"
            onOpen={(anchorEl) => setRenewalMenuAnchor(anchorEl)}
            wrapperClassName="xl:w-[260px]"
          />
          <ClearFiltersButton
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            wrapperClassName="xl:w-[220px]"
          />
        </FilterBar>
      </ManagePageSection>

      {!chamberId && (
        <Alert severity="info" className="mt-5">
          Chamber context is loading before member data can be shown.
        </Alert>
      )}

      {manageMembersError && (
        <Alert severity="error" className="mt-5">
          {manageMembersError.message ||
            "Could not load members from the backend."}
        </Alert>
      )}

      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        PaperProps={{
          sx: { borderRadius: "6px", px: 2, py: 1, minWidth: 260 },
        }}
      >
        {allStatuses.map((status) => (
          <MenuItem
            key={status}
            onClick={() => toggleStatus(status)}
            sx={{ gap: 2, py: 1.25 }}
          >
            <Checkbox
              checked={selectedStatuses.includes(status)}
              sx={{
                color: "#000000",
                p: 0,
                "& .MuiSvgIcon-root": { fontSize: 32 },
                "&.Mui-checked": { color: cpaGold },
              }}
            />
            <span className="text-lg text-black">{statusLabels[status]}</span>
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={renewalMenuAnchor}
        open={Boolean(renewalMenuAnchor)}
        onClose={() => setRenewalMenuAnchor(null)}
        PaperProps={{
          // overflow visible so the CalendarDateInput dropdown can extend
          // past the menu surface instead of being clipped.
          sx: { overflow: "visible", p: 2, width: 340 },
        }}
      >
        <div className="grid grid-cols-1 gap-3">
          <div>
            <p className="mb-1 text-sm font-bold text-black">
              Renewal Date From
            </p>
            <CalendarDateInput
              ariaLabel="Renewal date from"
              mode="fullDate"
              value={renewalDateFrom}
              onChange={setRenewalDateFrom}
            />
          </div>
          <div>
            <p className="mb-1 text-sm font-bold text-black">
              Renewal Date To
            </p>
            <CalendarDateInput
              ariaLabel="Renewal date to"
              mode="fullDate"
              value={renewalDateTo}
              onChange={setRenewalDateTo}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              onClick={() => {
                setRenewalDateFrom("");
                setRenewalDateTo("");
              }}
            >
              Clear
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={() => setRenewalMenuAnchor(null)}
              sx={{
                backgroundColor: cpaGold,
                fontWeight: 800,
                "&:hover": { backgroundColor: cpaGoldDark },
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </Menu>

      <ManagePageSection
        className="mt-10"
        title="Member Lists"
        description="View membership details."
      >
        <DataTable
          ariaLabel="manage members table"
          columns={columns}
          rows={members}
          getRowKey={(member) => member.id}
          sort={{
            column: sortColumn,
            direction: sortDirection,
            // Only sortable columns render sort labels, so the shared
            // table never requests a sort on the "actions" key.
            onRequestSort: (column) =>
              requestSort(column as SortableColumnKey),
          }}
          showLoadingRow={manageMembersLoading && allMembers.length === 0}
          showEmptyRow={!manageMembersLoading && members.length === 0}
        />
      </ManagePageSection>

      <Dialog
        open={Boolean(memberToArchive)}
        onClose={() => {
          if (!archivingMember) {
            setMemberToArchive(null);
          }
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "28px",
            maxWidth: 720,
            position: "relative",
            px: { xs: 3, sm: 6 },
            py: { xs: 4, sm: 5 },
          },
        }}
      >
        <DialogTitle className="p-0 text-center">
          <IconButton
            onClick={() => setMemberToArchive(null)}
            disabled={archivingMember}
            aria-label="Close delete member confirmation"
            sx={{
              backgroundColor: "#F31723",
              color: "white",
              height: 36,
              position: "absolute",
              right: 24,
              top: 24,
              width: 36,
              zIndex: 1,
              "&:hover": { backgroundColor: "#D90F1A" },
            }}
          >
            <CloseIcon sx={{ fontSize: 26 }} />
          </IconButton>
          <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-[#F31723] text-white">
            <DeleteOutlineIcon sx={{ fontSize: 54 }} />
          </div>
          <span className="mt-8 block text-3xl font-bold leading-tight text-black">
            Delete Member
          </span>
        </DialogTitle>
        <DialogContent className="px-0 pt-6">
          <p className="text-center text-lg leading-7 text-black">
            Are you sure you want to delete this member?
          </p>
          <p className="mx-auto mt-8 max-w-[600px] text-center text-lg leading-8 text-black">
            Deleting this member will remove them from your active member list
            and they will no longer be visible in your Chamber Website, or
            Chamber Perks App™ directories.
          </p>
          <p className="mx-auto mt-8 max-w-[620px] text-center text-lg leading-8 text-black">
            The member will lose access to their Member Dashboard, and their
            profile will be moved to Past Members in your Chamber Member Pro™
            Dashboard for record-keeping and viewing only.
          </p>
        </DialogContent>
        <DialogActions
          className="px-0 pb-0 pt-5"
          sx={{
            display: "flex",
            gap: 4,
            justifyContent: "center",
            width: "100%",
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          }}
        >
          <Button
            variant="contained"
            startIcon={
              archivingMember ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                <DeleteOutlineIcon sx={{ fontSize: 34 }} />
              )
            }
            disabled={archivingMember}
            onClick={handleArchiveConfirm}
            sx={{
              backgroundColor: "#F31723",
              borderRadius: "10px",
              boxShadow: "0 5px 12px rgba(0,0,0,0.25)",
              color: "white",
              fontSize: 18,
              fontWeight: 800,
              height: 54,
              minWidth: 180,
              textTransform: "uppercase",
              "& .MuiButton-startIcon": { mr: 1.5 },
              "&:hover": { backgroundColor: "#D90F1A" },
            }}
          >
            Delete
          </Button>
          <Button
            variant="contained"
            disabled={archivingMember}
            onClick={() => setMemberToArchive(null)}
            sx={{
              backgroundColor: commonStyles.charcoal,
              borderRadius: "10px",
              boxShadow: "0 5px 12px rgba(0,0,0,0.25)",
              color: "white",
              fontSize: 18,
              fontWeight: 800,
              height: 54,
              minWidth: 180,
              textTransform: "uppercase",
              "&:hover": { backgroundColor: "#111827" },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={archiveSuccessOpen}
        onClose={() => setArchiveSuccessOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "28px",
            maxWidth: 560,
            px: { xs: 4, sm: 6 },
            py: { xs: 7, sm: 8 },
          },
        }}
      >
        <DialogContent className="p-0 text-center">
          {/* Red circular close matches the mock's success pop-up. */}
          <IconButton
            onClick={() => setArchiveSuccessOpen(false)}
            aria-label="Close member deleted confirmation"
            sx={{
              backgroundColor: "#F31723",
              color: "white",
              height: 36,
              position: "absolute",
              right: 24,
              top: 24,
              width: 36,
              "&:hover": { backgroundColor: "#D90F1A" },
            }}
          >
            <CloseIcon sx={{ fontSize: 26 }} />
          </IconButton>
          <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-[#00BA2C] text-white">
            <CheckCircleOutlineIcon sx={{ fontSize: 66 }} />
          </div>
          <h2 className="mt-12 text-3xl font-bold leading-tight text-black">
            Member Deleted Successfully!
          </h2>
        </DialogContent>
      </Dialog>
    </ManagePageShell>
  );
};

export default ManageMembers;
